/**
 * featureAccess.ts
 *
 * Client-side type-safe constants for Feature Access Control feature keys.
 *
 * This file is the thin TypeScript mirror of the server-side registry at
 * helpers/featureAccessRegistry.js. It exists purely for type safety and
 * autocomplete – all business logic (defaults, labels, descriptions) lives
 * on the server.
 *
 * IMPORTANT: Keep this in sync with FEATURE_ACCESS_REGISTRY in
 * helpers/featureAccessRegistry.js. If you add a new feature key server-side,
 * add the corresponding constant here.
 */

export const FeatureKey = {
  /** Club Billing page (sidebar + route). */
  billing: "billing",
  /** Reports page (sidebar + route). */
  reports: "reports",
  /** Admin > Manage Club tab (/app/club/admin, tab 1). */
  clubAdminManageClub: "clubAdminManageClub",
  /** Admin > Event Orders tab (/app/club/admin, tab 3). */
  clubAdminEventOrders: "clubAdminEventOrders",
  /** Admin > Age Groups tab (/app/club/admin, tab 5). */
  adminAgeGroups: "adminAgeGroups",
  /** Manage Club Users page. */
  manageClubUsers: "manageClubUsers",
  /** Athlete profile icon in Manage Roster. */
  athleteProfile: "athleteProfile",
  /** Add Swim Meet button on Meet Schedule. */
  addSwimMeet: "addSwimMeet",
  /** Declarations tab in Meet Actions modal. */
  declarations: "declarations",
  /** Club Communication (announcements) drawer in the header. */
  clubCommunication: "clubCommunication",
  /** Multi-Session Meet option in the Create Meet flow. */
  multiSessionMeet: "multiSessionMeet",
} as const

export type FeatureKeyType = (typeof FeatureKey)[keyof typeof FeatureKey]

/**
 * The source (tier) that produced a lock on a feature.
 * Mirrors the scopeType ENUM in the DB; null means "not locked".
 */
export type LockSource =
  | "systemGuest"
  | "systemCustomer"
  | "org"
  | "club"
  | "clubSeason"
  | "league"
  | null
